import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, formatDate, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit, ArrowLeft, Building2, ChevronRight, AlertTriangle, Archive, ArchiveRestore } from "lucide-react";
import { DisputeBadge } from "@/components/admin/DisputeBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import SubcontractorDetail from "@/components/SubcontractorDetail";
import ReliabilityBadge from "@/components/ReliabilityBadge";
import { useTableColumns } from "@/hooks/useTableColumns";
import { ColumnsToggle, SortButton } from "@/components/TableControls";

const defaultForm = {
  company_name: "",
  first_name: "",
  last_name: "",
  email: "",
  phone_fixed: "",
  phone_mobile: "",
  address: "",
  tva_number: "",
  siret: "",
  hourly_rate: "",
  comments: "",
  order_type_ids: []
};

export const SubcontractorsPage = () => {
  const [subs, setSubs] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [watchlistOnly, setWatchlistOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState("name");  // name | score_asc | score_desc | sav_desc
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(defaultForm);

  // Configuration colonnes (tri + masquage)
  const SUB_COLUMNS = [
    { key: "name", label: "Société / Nom" },
    { key: "contact", label: "Contact" },
    { key: "phones", label: "Téléphones", sortable: false },
    { key: "types", label: "Types", sortable: false },
    { key: "reliability", label: "Fiabilité" },
    { key: "compliance", label: "Conformité" },
    { key: "orders_count", label: "Cmd." },
    { key: "total_contract_amount", label: "Total contrats" },
    { key: "actions", label: "Actions", sortable: false, always: true },
  ];
  const {
    isVisible: isSubColVisible,
    toggleVisibility: toggleSubCol,
    handleSort: handleSubSort,
    sortArrow: subSortArrow,
    sortConfig: subSortConfig,
    sortItems: sortSubs,
    orderedColumns: orderedSubColumns,
    moveColumn: moveSubColumn,
    resetOrder: resetSubOrder,
  } = useTableColumns({ storageKey: "subs-table", columns: SUB_COLUMNS });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [subRes, otRes, compRes] = await Promise.all([
        api.getSubcontractors({ include_archived: showArchived }),
        api.getOrderTypes(),
        api.getSubcontractorsComplianceSummary().catch(() => ({ data: { subcontractors: [] } })),
      ]);
      const complianceMap = {};
      (compRes.data?.subcontractors || []).forEach((c) => { complianceMap[c.id] = c; });
      const enriched = (subRes.data || []).map((s) => ({ ...s, compliance: complianceMap[s.id] || null }));
      setSubs(enriched);
      setOrderTypes(otRes.data);
    } catch (e) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleArchiveToggle = async (s) => {
    try {
      if (s.archived) {
        await api.unarchiveSubcontractor(s.id);
        toast.success("Sous-traitant réactivé");
      } else {
        await api.archiveSubcontractor(s.id);
        toast.success("Sous-traitant archivé");
      }
      fetchData();
    } catch {
      toast.error("Erreur archivage");
    }
  };

  const openCreate = () => {
    setFormData(defaultForm);
    setEditMode(false);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setFormData({ ...defaultForm, ...s, order_type_ids: s.order_type_ids || [] });
    setEditMode(true);
    setDialogOpen(true);
  };

  const toggleOT = (id) => {
    setFormData(f => ({
      ...f,
      order_type_ids: f.order_type_ids.includes(id) ? f.order_type_ids.filter(x => x !== id) : [...f.order_type_ids, id]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.company_name && !formData.last_name) {
      toast.error("Société ou nom obligatoire");
      return;
    }
    try {
      const payload = {
        ...formData,
        hourly_rate: formData.hourly_rate === "" ? null : parseFloat(formData.hourly_rate),
      };
      if (editMode) {
        await api.updateSubcontractor(formData.id, payload);
        toast.success("Sous-traitant mis à jour");
      } else {
        await api.createSubcontractor(payload);
        toast.success("Sous-traitant créé");
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteSubcontractor(id);
      toast.success("Sous-traitant supprimé");
      if (selected?.id === id) setSelected(null);
      fetchData();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  if (selected) {
    return <SubcontractorDetail sub={selected} orderTypes={orderTypes} onBack={() => { setSelected(null); fetchData(); }} onEdit={() => openEdit(selected)} />;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="subcontractors-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold">Sous-traitants</h1>
          <p className="text-muted-foreground">Gérez vos sous-traitants et leurs contrats</p>
        </div>
        <Button onClick={openCreate} data-testid="add-subcontractor-btn"><Plus className="h-4 w-4 mr-2" /> Ajouter</Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle>Liste ({subs.length})</CardTitle>
            <div className="flex flex-wrap items-center gap-3" data-testid="sub-toolbar">
              <div className="flex items-center gap-2">
                <Switch
                  id="watchlist-toggle"
                  checked={watchlistOnly}
                  onCheckedChange={setWatchlistOnly}
                  data-testid="sub-watchlist-toggle"
                />
                <Label htmlFor="watchlist-toggle" className="cursor-pointer text-sm flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-amber-600" /> À surveiller uniquement
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="show-archived-subs"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                  data-testid="show-archived-subs-toggle"
                />
                <Label htmlFor="show-archived-subs" className="cursor-pointer text-sm flex items-center gap-1">
                  <Archive className="h-4 w-4 text-slate-500" /> Afficher archivés
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="sort-select" className="text-sm">Tri</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-8 w-[180px]" data-testid="sub-sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Nom (A → Z)</SelectItem>
                    <SelectItem value="score_asc">Score croissant</SelectItem>
                    <SelectItem value="score_desc">Score décroissant</SelectItem>
                    <SelectItem value="sav_desc">SAV (décroissant)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            let list = [...subs];
            if (watchlistOnly) {
              list = list.filter((s) => {
                const score = s.reliability?.score;
                return score != null && score < 80;
              });
            }
            list.sort((a, b) => {
              if (sortBy === "score_asc") return (a.reliability?.score ?? 100) - (b.reliability?.score ?? 100);
              if (sortBy === "score_desc") return (b.reliability?.score ?? 0) - (a.reliability?.score ?? 0);
              if (sortBy === "sav_desc") return (b.reliability?.sav_12m ?? 0) - (a.reliability?.sav_12m ?? 0);
              const an = (a.company_name || `${a.first_name || ""} ${a.last_name || ""}`).toLowerCase();
              const bn = (b.company_name || `${b.first_name || ""} ${b.last_name || ""}`).toLowerCase();
              return an.localeCompare(bn);
            });
            // Si l'utilisateur a cliqué sur une en-tête, le tri colonne prend le dessus
            if (subSortConfig) {
              list = sortSubs(list, {
                name: (s) => s.company_name || `${s.first_name || ""} ${s.last_name || ""}`.trim(),
                contact: (s) => `${s.first_name || ""} ${s.last_name || ""}`.trim(),
                reliability: (s) => s.reliability?.score ?? 0,
              });
            }

            if (list.length === 0) {
              return (
                <p className="text-center text-muted-foreground py-8" data-testid="sub-empty">
                  {watchlistOnly ? "Aucun sous-traitant à surveiller 👌" : "Aucun sous-traitant."}
                </p>
              );
            }

            return (
              <>
              <div className="flex items-center justify-end pb-2">
                <ColumnsToggle columns={SUB_COLUMNS} orderedColumns={orderedSubColumns} isVisible={isSubColVisible} toggleVisibility={toggleSubCol} moveColumn={moveSubColumn} resetOrder={resetSubOrder} testId="subs-columns-toggle" />
              </div>
              <Table>
              <TableHeader>
                <TableRow>
                  {orderedSubColumns.filter(c => isSubColVisible(c.key)).map(col => {
                    if (col.key === 'phones') return <TableHead key={col.key}>Téléphones</TableHead>;
                    if (col.key === 'types') return <TableHead key={col.key}>Types</TableHead>;
                    if (col.key === 'actions') return <TableHead key={col.key}></TableHead>;
                    return (
                      <TableHead key={col.key}>
                        <SortButton columnKey={col.key} label={col.label} sortArrow={subSortArrow} handleSort={handleSubSort} />
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map(s => {
                  const renderSubCell = (key) => {
                    switch(key) {
                      case 'name': return (
                        <TableCell key={key} className="font-medium">
                          <div className="flex items-center gap-2">
                            <button onClick={() => setSelected(s)} className="text-primary hover:underline text-left">
                              {s.company_name || `${s.first_name || ""} ${s.last_name || ""}`.trim()}
                            </button>
                            <DisputeBadge thirdPartyId={s.id} />
                            {s.archived && (
                              <Badge variant="outline" className="text-[10px] border-slate-400 text-slate-600" data-testid={`archived-badge-sub-${s.id}`}>Archivé</Badge>
                            )}
                          </div>
                        </TableCell>
                      );
                      case 'contact': return (
                        <TableCell key={key}>{`${s.first_name || ""} ${s.last_name || ""}`.trim() || "-"}</TableCell>
                      );
                      case 'phones': return (
                        <TableCell key={key} className="text-xs">
                          {s.phone_mobile && <div>M: {s.phone_mobile}</div>}
                          {s.phone_fixed && <div>F: {s.phone_fixed}</div>}
                          {s.email && <div className="text-muted-foreground truncate max-w-[200px]" title={s.email}>{s.email}</div>}
                          {!s.phone_mobile && !s.phone_fixed && !s.email && "-"}
                        </TableCell>
                      );
                      case 'types': return (
                        <TableCell key={key}>
                          <div className="flex flex-wrap gap-1">
                            {(s.order_type_ids || []).slice(0, 3).map(id => {
                              const ot = orderTypes.find(o => o.id === id);
                              return ot ? <Badge key={id} variant="secondary">{ot.name}</Badge> : null;
                            })}
                            {(s.order_type_ids || []).length > 3 && <Badge variant="outline">+{(s.order_type_ids || []).length - 3}</Badge>}
                          </div>
                        </TableCell>
                      );
                      case 'reliability': return (
                        <TableCell key={key}><ReliabilityBadge reliability={s.reliability} /></TableCell>
                      );
                      case 'compliance': return (
                        <TableCell key={key} data-testid={`sub-compliance-cell-${s.id}`}>
                          {(() => {
                            const c = s.compliance;
                            if (!c || c.required_count === 0) {
                              return <span className="text-xs text-muted-foreground">—</span>;
                            }
                            const ok = c.present_count === c.required_count && c.status === "ok";
                            const cls = ok
                              ? "bg-emerald-100 text-emerald-700"
                              : (c.status === "expired" || c.status === "incomplete")
                                ? "bg-red-100 text-red-700"
                                : "bg-amber-100 text-amber-700";
                            const label = c.status === "expired"
                              ? "Expiré"
                              : c.status === "expiring_soon"
                                ? "Expire bientôt"
                                : c.status === "incomplete" ? "Incomplet" : "OK";
                            return (
                              <div className="flex flex-col gap-0.5">
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium w-fit ${cls}`}>
                                  {c.present_count}/{c.required_count}
                                </span>
                                <span className="text-[10px] text-muted-foreground">{label}</span>
                              </div>
                            );
                          })()}
                        </TableCell>
                      );
                      case 'orders_count': return <TableCell key={key}>{s.orders_count || 0}</TableCell>;
                      case 'total_contract_amount': return <TableCell key={key}>{formatCurrency(s.total_contract_amount || 0)}</TableCell>;
                      case 'actions': return (
                        <TableCell key={key}>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setSelected(s)}><ChevronRight className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(s)}><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleArchiveToggle(s)} data-testid={`archive-sub-${s.id}`} title={s.archived ? "Restaurer" : "Archiver"}>
                              {s.archived ? <ArchiveRestore className="h-4 w-4 text-emerald-600" /> : <Archive className="h-4 w-4 text-slate-500" />}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild><Button variant="ghost" size="sm"><Trash2 className="h-4 w-4 text-red-500" /></Button></AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Supprimer ce sous-traitant ?</AlertDialogTitle><AlertDialogDescription>Les commandes seront dissociées (pas supprimées).</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(s.id)}>Supprimer</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      );
                      default: return null;
                    }
                  };
                  return (
                    <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                      {orderedSubColumns.filter(col => isSubColVisible(col.key)).map(col => renderSubCell(col.key))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </>
            );
          })()}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? "Modifier le sous-traitant" : "Nouveau sous-traitant"}</DialogTitle>
            <DialogDescription>Coordonnées et types de commandes réalisables</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Nom de la société</Label>
              <Input value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} data-testid="sub-company" />
            </div>
            <div><Label>Prénom</Label><Input value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} /></div>
            <div><Label>Nom</Label><Input value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} /></div>
            <div><Label>Téléphone fixe</Label><Input value={formData.phone_fixed} onChange={e => setFormData({...formData, phone_fixed: e.target.value})} /></div>
            <div><Label>Téléphone mobile</Label><Input value={formData.phone_mobile} onChange={e => setFormData({...formData, phone_mobile: e.target.value})} /></div>
            <div className="md:col-span-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="contact@sous-traitant.fr"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                data-testid="sub-email"
              />
              <p className="text-xs text-muted-foreground mt-1">Utilisé pour les communications et notifications.</p>
            </div>
            <div className="md:col-span-2"><Label>Adresse</Label><Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} /></div>
            <div><Label>Numéro TVA</Label><Input value={formData.tva_number} onChange={e => setFormData({...formData, tva_number: e.target.value})} /></div>
            <div><Label>SIRET</Label><Input value={formData.siret} onChange={e => setFormData({...formData, siret: e.target.value})} /></div>
            <div>
              <Label>Taux horaire (€/h)</Label>
              <Input
                type="number" step="0.01" min="0"
                placeholder="Ex : 45.00"
                value={formData.hourly_rate ?? ""}
                onChange={e => setFormData({ ...formData, hourly_rate: e.target.value })}
                data-testid="sub-hourly-rate"
              />
              <p className="text-xs text-muted-foreground mt-1">Utilisé pour calculer le coût main-d'œuvre dans le rapport de rentabilité.</p>
            </div>
            <div className="md:col-span-2">
              <Label>Types de commandes réalisables</Label>
              <div className="border rounded-lg p-3 mt-1 space-y-2 max-h-48 overflow-y-auto">
                {orderTypes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun type de commande. Créez-en d'abord.</p>
                ) : orderTypes.map(ot => (
                  <div key={ot.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`ot-${ot.id}`}
                      checked={formData.order_type_ids.includes(ot.id)}
                      onCheckedChange={() => toggleOT(ot.id)}
                      data-testid={`sub-ot-${ot.id}`}
                    />
                    <label htmlFor={`ot-${ot.id}`} className="text-sm cursor-pointer">{ot.name}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Commentaires</Label>
              <Textarea value={formData.comments} onChange={e => setFormData({...formData, comments: e.target.value})} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSubmit} data-testid="save-sub-btn">{editMode ? "Mettre à jour" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubcontractorsPage;
